/**
 * 66.9, cover slots (the owner, Oct 12: "for the square stacked rocks, the oblate stacked rocks, the big rectangular slabs
 * (when > 1.5 player width), the leans either don't reach around enough to stick out, or the character walks all the way
 * around the edge of the object and is fully exposed … maybe some concept of cover slots on coverable objects and you snap
 * into a slot that has the right distances already sorted out?") — lane F's half: **where a body takes cover at a wide
 * prop**. Supervisor-1's take-cover snaps to a slot and the lean reads its side; the bots' posts can read the same slots.
 *
 * - **The hull** of a biome rock is the drawn rock where a pressed body meets it: its kind's drawn variant (core/treeDraws
 *   rockVariant, the stands' prototype) measured as a rectangle (constants NORMAL_ROCK_HULL), turned on the ground by the
 *   drawn yaw (rockYaw) in the drawing's own frame (treeFrame). The sim's walk and shots still read the rock's disc and
 *   profile (step 3, the walk following the hull, is the owner's call): so a body at a wide face stands off it by the disc
 *   less the face's half-depth, and `gap` says how far. And a shot meets the rock's round profile: so an end slot's edge
 *   is the drawn hull's or that profile's at the lean's eyes (over the slot's ground), whichever reaches farther — the
 *   lean clears what stops shots, and the body behind it is still behind the rock.
 * - **The slots**, per face of the hull: an **end** slot a lean's reach inside each edge (the lean comes out past the edge,
 *   its side known); on a face wider than COVER_SLOTS.wideBodies player widths a **middle** slot (no lean; `peekOver` when
 *   a standing eye sees over); a face too narrow for its ends to part, both at its middle. A rock gets slots only when its hull is wide —
 *   trees and narrow rocks keep the cover points (core/coverMap.ts), which work at them (the owner).
 * - **Where the body stands** (`dir`): pressed to the hull (its half-width and COVER_SLOTS.pressBodies), and never inside
 *   the walk's own disc of its rock or any other prop's, nor a building — so a walk reaches it.
 * - **Shelter and the peek over** are the cover points' own lines (coverMap.ts coverLines).
 *
 * Pure and deterministic (dmath; a fixed order; the slots sorted by key), cached per planet while its shapes, its walk
 * footprints and the config are the same objects. Not state: nothing here enters the match, its checksum or its saves.
 */
import type { CoverSlot, GameState, PlanetState, RockState, Vec3 } from '../types';
import { NORMAL_ROCK_HULL, NORMAL_ROCK_PERSONS, NORMAL_ROCK_SIZE, ROCK_SINK_FRACTION } from '../constants';
import { vecAdd, vecCross, vecDot, vecLength, vecNormalize, vecScale } from '../math';
import { clearOfCollapse } from './collapseHazard';
import { dacos, dcos, dsin } from './dmath';
import { obstaclesNear, planetObstacles, projectToTangent, type PlanetObstacles, type WalkObstacle } from './surfaceWalk';
import { insideBuilding, planetShapes, shapesWithin, type PlanetShapes } from './hits';
import { coverAcrossOn, coverLines, COVER_POINTS } from './coverMap';
import { personSurfaceRadius } from './fineTerrain';
import { personSetOf, PERSON_BODY_HEIGHT, type PersonSet } from './scaleProfile';
import { leanReachPeople } from './lean';
import { stanceEyeScale } from './stance';
import { getSurfaceRadius } from './surface';
import { seatRadius } from './propSeat';
import { rockVariant, rockYaw, treeFrame } from './treeDraws';

/** The dials (people: × the scale's person height; bodies: × its unit radius). */
export const COVER_SLOTS = {
  /** A face wider than this many player widths (2 × the unit radius) is wide: a middle slot, its ends a lean inside its edges; a rock gets slots only with one. */
  wideBodies: 1.5,
  /** An end slot stands a lean's reach (core/lean.ts, the edge pose's) inside its edge, less this: the leaned muzzle clears the edge by it. */
  leanClearPeople: 0.08,
  /** The body pressed to the hull: its half width and this many body radii more (take-cover's TAKE_COVER.pressBodies). */
  pressBodies: 0.05,
} as const;

type SlotState = Pick<GameState, 'planets' | 'config' | 'rocks' | 'flora' | 'structures'> & Partial<Pick<GameState, 'ruins'>>;

const arcOn = (a: Vec3, b: Vec3, R: number) => dacos(Math.max(-1, Math.min(1, vecDot(a, b)))) * R;
/** The unit direction a tangent offset `p` (world units, at `up`) along the ground from `up` on a planet of radius `R`. */
function along(up: Vec3, p: Vec3, R: number): Vec3 {
  const len = vecLength(p);
  if (len < 1e-12) return up;
  const th = len / R;
  return vecNormalize(vecAdd(vecScale(up, dcos(th)), vecScale(p, dsin(th) / len)));
}

/** A rock's half-width as the shots read it at `h` over the ground (its profile × its radius, the sink below; else its radius). */
function profileHalf(o: WalkObstacle, h: number): number {
  if (!o.profile?.length || !(o.height > 0)) return o.radius;
  const k = o.profile.length, x = Math.max(0, Math.min(k - 1, (h / o.height + (o.sink ?? 0)) * (k - 1)));
  const i = Math.min(k - 2, Math.floor(x)), f = x - i;
  return o.radius * (o.profile[i] * (1 - f) + o.profile[i + 1] * f);
}

/** The rocks by id, per rocks array. */
const rockIndex = new WeakMap<readonly RockState[], Map<number, RockState>>();
function rockById(rocks: readonly RockState[], id: number): RockState | undefined {
  let m = rockIndex.get(rocks);
  if (!m) { m = new Map(rocks.map(r => [r.id, r])); rockIndex.set(rocks, m); }
  return m.get(id);
}

/** Per planet's shapes: the slots by rock, while its walk footprints and the config are the very objects they were made against. */
let kept = new WeakMap<PlanetShapes, { walk: PlanetObstacles; config: unknown; byRock: Map<string, readonly CoverSlot[]> }>();
/** For the tests: run `fn` with nothing kept, then put back what was. */
export function coverSlotsFromNothing<T>(fn: () => T): T {
  const saved = kept;
  kept = new WeakMap();
  try { return fn(); } finally { kept = saved; }
}

/**
 * The cover slots within `radius` (world units, along the ground) of `dir` on planet `planetIndex`, sorted by key: at
 * every wide biome rock whose slots could fall there.
 */
export function coverSlotsNear(state: SlotState, planetIndex: number, dir: Vec3, radius: number): CoverSlot[] {
  const planet = state.planets[planetIndex];
  if (!planet || !state.rocks?.length) return [];
  const ps = personSetOf(state);
  const walk = planetObstacles(state, planetIndex);
  if (walk.list.length === 0) return [];
  const shapes = planetShapes(state, planetIndex);
  let k = kept.get(shapes);
  if (!k || k.walk !== walk || k.config !== state.config) { k = { walk, config: state.config, byRock: new Map() }; kept.set(shapes, k); }
  const R = planet.radius, n = vecNormalize(dir);
  // (a slot stands at most a rock's disc, a pressed body and the hull's corner off its rock's foot)
  const out: CoverSlot[] = [];
  for (const o of obstaclesNear(walk, n, (radius + 2 * walk.maxRadius + 2 * ps.unitRadius) / R)) {
    if (o.ref?.kind !== 'rock') continue;
    if (arcOn(o.dir, n, R) > radius + 2 * o.radius + 2 * ps.unitRadius) continue;
    const rock = rockById(state.rocks, o.ref.id);
    if (!rock || !rock.alive || rock.size !== 'normal' || !NORMAL_ROCK_HULL[rock.rockType]) continue;
    const key = `${rock.id}|${o.dir.x}|${o.dir.y}|${o.dir.z}|${o.radius}|${rock.scale}`;
    let slots = k.byRock.get(key);
    if (!slots) { slots = slotsOf(state, planetIndex, planet, ps, walk, shapes, o, rock); k.byRock.set(key, slots); }
    for (const s of slots) if (arcOn(s.dir, n, R) <= radius) out.push(s);
  }
  return clearOfCollapse(state, planetIndex, out.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0)));   // (72.3 step 7: none in a building coming down)
}

/** A wide rock's slots: per face of its hull, its ends and (wide) its middle; none for a narrow rock. */
function slotsOf(state: SlotState, planetIndex: number, planet: PlanetState, ps: PersonSet, walk: PlanetObstacles, shapes: PlanetShapes, o: WalkObstacle, rock: RockState): readonly CoverSlot[] {
  const person = PERSON_BODY_HEIGHT * ps.k, R = planet.radius;
  const hull = NORMAL_ROCK_HULL[rock.rockType][rockVariant(rock.id)];
  // the rock's model units to the world's: the walk disc is NORMAL_ROCK_SIZE.half of them, so the same scale (surfaceWalk.ts)
  // (74.4: a lying rock's own disc is its hull's depth, not its half: its scale as surfaceWalk.ts takes it)
  const S = o.lyingFrom !== undefined ? rock.scale * NORMAL_ROCK_PERSONS * person : o.radius / (NORMAL_ROCK_SIZE[rock.rockType]?.half ?? NaN);
  const wide = (halfWidth: number) => 2 * halfWidth > COVER_SLOTS.wideBodies * 2 * ps.unitRadius;
  if (!hull || !(S > 0) || !wide(Math.max(hull.hx, hull.hz) * S)) return [];
  // the hull on the ground: the model's axes in the drawing's frame (the yaw about the rock's up, then up)
  const up = vecNormalize(o.dir), frame = treeFrame(up, rockYaw(rock.id));
  const ct = dcos(hull.turn), st = dsin(hull.turn);
  const U = frame(ct, 0, st), W = frame(-st, 0, ct), C = vecScale(frame(hull.cx, 0, hull.cz), S);
  const press = ps.unitRadius * (1 + COVER_SLOTS.pressBodies);
  const contact = o.radius + press;   // the walk's own disc of the rock, and a pressed body
  const inset = (leanReachPeople('edge') - COVER_SLOTS.leanClearPeople) * person;
  // the shots' own edge: the rock's profile (core/hits.ts, a frustum a tenth of its height over the coarse ground at its
  // foot) at the leaned eyes — crouched and standing, lifted by the slot's own ground over the rock's (a slope)
  const rockGround = seatRadius(planet, o.seat, up, ps), eyes = [ps.losEyeHeight * stanceEyeScale('crouch'), ps.losEyeHeight];   // (71.2: the rock's seat, as its shape stands)
  const shotHalf = (lift: number) => Math.max(...eyes.map(e => profileHalf(o, e + lift)));
  // the lines a slot's shelter reads: its rock's, as the cover points take them (its ring, a side step, the run past it)
  const ring = o.radius + COVER_POINTS.clearanceBodies * ps.unitRadius + COVER_POINTS.marginK * ps.k;
  const reach = ring + Math.max(...COVER_POINTS.sideSteps) * 2 * ps.unitRadius + (2 * o.radius + COVER_POINTS.pastPeople * person);
  const centre = vecAdd(planet.position, vecScale(up, personSurfaceRadius(planet, up, ps)));
  const { meets, shelterAround } = coverLines(planet, shapes, shapesWithin(centre, reach + person, planet, shapes), ps, coverAcrossOn(state.config));
  const others = obstaclesNear(walk, up, (contact + 2 * walk.maxRadius + 2 * ps.unitRadius) / R).filter(q => q !== o);
  const top = (o.height * (1 - ROCK_SINK_FRACTION)) / person;
  const out: CoverSlot[] = [];
  const faces: Array<{ N: Vec3; depth: number; half: number }> = [
    { N: U, depth: hull.hx * S, half: hull.hz * S }, { N: vecScale(U, -1), depth: hull.hx * S, half: hull.hz * S },
    { N: W, depth: hull.hz * S, half: hull.hx * S }, { N: vecScale(W, -1), depth: hull.hz * S, half: hull.hx * S },
  ];
  faces.forEach((f, fi) => {
    // about the rock's own centre (its walk disc's, its shots' profile's): the hull's face and edges where its centre puts
    // them, the shots' edges at ± their half-width; each end a lean inside the farther, the middle at the hull's
    const facing0 = vecScale(f.N, -1), along0 = vecNormalize(vecCross(facing0, up));
    const ca = vecDot(C, along0), face = vecDot(C, f.N) + f.depth;
    // pressed to the face; out to the walk's disc where the disc reaches past the face there
    const place = (a: number) => { const m = Math.max(face + press, Math.sqrt(Math.max(0, contact * contact - a * a))); return { m, at: along(up, vecAdd(vecScale(along0, a), vecScale(f.N, m)), R) }; };
    // an end: a lean inside the farther edge — the hull's, or the shots' at the eyes over the slot's own ground (its
    // place read once from where the rock's ground would put it, then again from there)
    const endAt = (sign: 1 | -1): number => {
      const hullEdge = sign > 0 ? ca + f.half : f.half - ca, a = (lift: number) => sign * Math.max(0, Math.max(hullEdge, shotHalf(lift)) - inset);
      const first = a(personSurfaceRadius(planet, up, ps) - rockGround);
      return a(personSurfaceRadius(planet, place(first).at, ps) - rockGround);
    };
    const spots: Array<{ a: number; tag: 'e+' | 'e-' | 'm'; lean?: 1 | -1 }> = [{ a: endAt(1), tag: 'e+', lean: 1 }, { a: endAt(-1), tag: 'e-', lean: -1 }];
    if (wide(f.half)) spots.push({ a: ca, tag: 'm' });
    for (const sp of spots) {
      const { m, at } = place(sp.a);
      if (others.some(x => arcOn(at, x.dir, R) < x.radius + ps.unitRadius)) continue;   // another prop's walk disc: no body stands there
      if (insideBuilding(state, planetIndex, at)) continue;
      const facing = vecNormalize(projectToTangent(facing0, at)), side = vecNormalize(vecCross(facing, at));
      const ground = personSurfaceRadius(planet, at, ps), L = arcOn(at, o.dir, R) + o.radius + COVER_POINTS.pastPeople * person;
      const shelter = shelterAround(at, facing, ground, L);
      if (!shelter[0]) continue;   // hides nobody straight across the rock: not cover
      out.push({
        planetIndex, key: `${planetIndex}:rock:${rock.id}:${fi}:${sp.tag}`, prop: { kind: 'rock', id: rock.id },
        dir: at, facing, along: side, kind: sp.tag === 'm' ? 'middle' : 'end', ...(sp.lean ? { lean: sp.lean } : {}),
        peekOver: !meets(at, ground, ps.losEyeHeight, facing, L), top, gap: (m - face) / person, faceWidth: (2 * f.half) / person, shelter,
      });
    }
  });
  return out.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
}
